import {
  Button,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { actions as personalActions } from 'components/settings/actions/personal';
import microphonePermissions from 'images/issus-fixes/microphone_issue_permission_denied.gif';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './microphoneErrorModal.module.scss';
import {
  microphoneErrorMessages,
  microphoneErrorSolutionMessages,
} from './utils/microphoneMessages';

/** A component that displays a modal when the microphone has an error. */
export const MicrophoneErrorModal = createAppModal(
  { type: 'app:microphoneError' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const dispatch = useAppDispatch();

    /**
     * Closes the modal and potentially remembers the user choice.
     *
     * @param remember Whether to not show the modal again.
     */
    const handleClose = (remember = false) => {
      dispatch(
        personalActions.personalToggleRememberMicrophoneAccess({
          value: remember,
        }),
      );

      hide?.();
    };

    /** The error message. */
    const errorMessage = () => {
      if (modal.arg.error) {
        return (
          <Text.Paragraph>
            {microphoneErrorMessages[modal.arg.error]}
          </Text.Paragraph>
        );
      }
      return undefined;
    };

    /** The solution to the error. */
    const errorSolution = useMemo(() => {
      return (
        <section className={styles.section}>
          {modal.arg.error === 'PermissionDeniedError' ||
          modal.arg.error === 'NotAllowedError' ? (
            <img
              src={microphonePermissions}
              className={styles.image}
              alt="Microphone permissions demonstration"
            />
          ) : (
            <>
              <Text.Heading variant="heading-3" className={styles.sectionTitle}>
                Why is my microphone not working?
              </Text.Heading>

              {microphoneErrorSolutionMessages.map(
                ({ title, description }, index) => (
                  <section key={index} className={styles.solution}>
                    <Text.Heading
                      variant="heading-4"
                      className={styles.solutionTitle}
                    >
                      {title}
                    </Text.Heading>
                    <Text.Paragraph>{description}</Text.Paragraph>
                  </section>
                ),
              )}
            </>
          )}
        </section>
      );
    }, [modal]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={() => handleClose()}
        className={styles.modal}
        wrapProps={{ className: 'print-hidden' }}
      >
        <ModalHead title="Microphone issue" onClose={() => handleClose()} />

        <ModalBody>
          <section className={styles.section}>
            <Text.Paragraph>
              There is an issue with your Microphone and you cannot make and
              receive phone calls. Follow the instructions below and reload the
              page.
            </Text.Paragraph>

            {errorMessage()}
          </section>

          {errorSolution}
        </ModalBody>

        <ModalFoot>
          <Tooltip content="Dixa will not try to access the microphone. You can always enable access by click on the phone channel icon.">
            <Button variant="secondary" onClick={() => handleClose(true)}>
              {`Dismiss and don't remind me again`}
            </Button>
          </Tooltip>
          <Button
            className={styles.dismissAction}
            variant="primary"
            onClick={() => handleClose()}
          >
            Dismiss
          </Button>
        </ModalFoot>
      </Modal>
    );
  },
);
